import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { buildTourSteps } from './tourDefinitions';

let activeDriver = null;

/** True for the entire lifetime of a guided-tour run — lets the app skip side effects
 * (like creating a real, persistent live meeting session) that should never happen
 * just from previewing screens in a tour. */
export function isTourActive() {
  return activeDriver !== null;
}

function resolveRoute(template, orgId) {
  if (!template) return null;
  return template.replace(':orgId', orgId || '');
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function waitForElement(selector, { timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(timer);
        resolve(el);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        resolve(null);
      }
    }, 80);
  });
}

function dispatchTourSafeDismiss() {
  window.dispatchEvent(
    new CustomEvent('pp-tour-dismiss-meeting-wizard', { detail: { tourSafe: true } })
  );
}

export function destroyActiveTour() {
  if (activeDriver) {
    try {
      activeDriver.destroy();
    } catch {
      /* ignore */
    }
    activeDriver = null;
  }
}

/**
 * Run a tour one step at a time: navigate → wait for anchor → spotlight.
 * (Pre-walking all routes then drive() left the user on the wrong page.)
 *
 * @param {{ tourId: string, orgId: string, navigate: (path: string) => void, onFinished: (r: { completed: boolean, tourId: string }) => void }} opts
 */
export async function runTour({ tourId, orgId, navigate, onFinished }) {
  destroyActiveTour();

  const prepared = buildTourSteps(tourId).map((step) => ({
    ...step,
    path: resolveRoute(step.route, orgId),
  }));

  if (!prepared.length) {
    onFinished?.({ completed: false, tourId });
    return;
  }

  let completedFully = false;
  let finishedNotified = false;
  let cancelled = false;
  let currentIndex = 0;
  let showGeneration = 0;

  const notify = (completed) => {
    if (finishedNotified) return;
    finishedNotified = true;
    cancelled = true;
    activeDriver = null;
    onFinished?.({ completed, tourId });
  };

  activeDriver = driver({
    animate: true,
    overlayOpacity: 0.55,
    stagePadding: 8,
    stageRadius: 10,
    allowClose: true,
    smoothScroll: true,
    onCloseClick: (_el, _step, { driver: d }) => {
      completedFully = false;
      d.destroy();
    },
    onDestroyed: () => {
      notify(completedFully);
    },
  });

  const isStale = (gen) => cancelled || !activeDriver || gen !== showGeneration;

  const runAction = async (action, step) => {
    if (!action) return;
    if (action === 'electionScreen' && typeof step.screen === 'number') {
      // Switch the live session's internal view to Elections first — highlighting a nav
      // tab never clicks it, and the actual elections screens only render once currentView
      // is 'elections'. Route navigation alone doesn't get you here from inside a session.
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-elections-view'));
      await sleep(300);
      window.dispatchEvent(
        new CustomEvent('pp-tour-election-screen', { detail: { screen: step.screen } })
      );
      await sleep(400);
    } else if (action === 'openCommitteesView') {
      // For steps that just need to look at the Committees page itself, with no
      // committee-specific setup (create form / cards / detail) beyond that.
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committees-view'));
      await sleep(300);
    } else if (action === 'openCommitteeCreate') {
      // Same fix as electionScreen above: switch the live session's internal view to
      // Committees first, since highlighting the nav tab never clicks it.
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committees-view'));
      await sleep(300);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committee-create'));
      await sleep(350);
    } else if (action === 'prepareCommitteeCards') {
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committees-view'));
      await sleep(300);
      window.dispatchEvent(new CustomEvent('pp-tour-prepare-committee-cards'));
      await sleep(400);
    } else if (action === 'selectFirstCommittee') {
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committees-view'));
      await sleep(300);
      window.dispatchEvent(new CustomEvent('pp-tour-select-first-committee'));
      await sleep(400);
    } else if (action === 'openCommitteeMeetingForTour') {
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committees-view'));
      await sleep(300);
      window.dispatchEvent(new CustomEvent('pp-tour-open-committee-meeting'));
      await sleep(500);
    } else if (action === 'dismissMeetingWizard') {
      window.dispatchEvent(new CustomEvent('pp-tour-open-meeting-view'));
      await sleep(200);
      dispatchTourSafeDismiss();
      await sleep(350);
    } else if (action === 'openMeetingWizard') {
      window.dispatchEvent(new CustomEvent('pp-tour-clear-meeting-demo'));
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-meeting-wizard'));
      await sleep(350);
    } else if (action === 'clearMeetingDemo') {
      window.dispatchEvent(new CustomEvent('pp-tour-clear-meeting-demo'));
      await sleep(300);
    } else if (action === 'seedMeetingDemo') {
      window.dispatchEvent(new CustomEvent('pp-tour-seed-meeting-demo'));
      await sleep(450);
    } else if (action === 'setTourAgenda') {
      window.dispatchEvent(new CustomEvent('pp-tour-open-meeting-view'));
      await sleep(200);
      dispatchTourSafeDismiss();
      await sleep(250);
      window.dispatchEvent(
        new CustomEvent('pp-tour-set-agenda', {
          detail: {
            match: step.agendaMatch || 'Call to Order',
            clearMotion: step.clearMotion !== false,
          },
        })
      );
      await sleep(400);
    } else if (action === 'prepareMeetingConsole') {
      window.dispatchEvent(new CustomEvent('pp-tour-open-meeting-view'));
      await sleep(200);
      dispatchTourSafeDismiss();
      await sleep(300);
      window.dispatchEvent(new CustomEvent('pp-tour-seed-meeting-demo'));
      await sleep(450);
    } else if (action === 'openObserverView') {
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-open-observer'));
      await sleep(500);
    } else if (action === 'prepareAssistedSession') {
      window.dispatchEvent(new CustomEvent('pp-tour-open-meeting-view'));
      await sleep(200);
      dispatchTourSafeDismiss();
      await sleep(200);
      window.dispatchEvent(new CustomEvent('pp-tour-set-assisted-session'));
      await sleep(400);
    } else if (action === 'openMeetingView') {
      window.dispatchEvent(new CustomEvent('pp-tour-open-meeting-view'));
      await sleep(350);
    } else if (action === 'openChairDashboardForTour') {
      window.dispatchEvent(new CustomEvent('pp-tour-open-chair-dashboard'));
      await sleep(500);
    }
  };

  const showStep = async (index) => {
    const gen = ++showGeneration;
    if (isStale(gen)) return;

    if (index < 0) index = 0;
    if (index >= prepared.length) {
      completedFully = true;
      activeDriver.destroy();
      return;
    }

    currentIndex = index;
    const step = prepared[index];
    const isLast = index === prepared.length - 1;

    if (step.path && typeof navigate === 'function') {
      const current = window.location.pathname;
      if (current !== step.path) {
        navigate(step.path);
        await sleep(500);
      }
    }

    if (isStale(gen)) return;

    const actions = Array.isArray(step.actions)
      ? step.actions
      : step.action
        ? [step.action]
        : [];
    for (const action of actions) {
      if (isStale(gen)) return;
      await runAction(action, step);
    }

    if (isStale(gen)) return;

    const el = await waitForElement(step.element, {
      timeoutMs: step.optional ? 2000 : 8000,
    });

    if (isStale(gen)) return;

    if (!el) {
      if (step.optional) {
        await showStep(index + 1);
        return;
      }
      // Non-optional missing: still show popover centered so the tour can continue
      activeDriver.highlight({
        popover: {
          title: step.popover.title,
          description: `${step.popover.description}\n\n(Couldn’t find this control on screen — use Next to continue, or open the right page and restart from Help.)`,
          side: 'over',
          align: 'center',
          showButtons: ['next', 'previous', 'close'],
          nextBtnText: isLast ? 'Done' : 'Next',
          prevBtnText: 'Back',
          progressText: `${index + 1} of ${prepared.length}`,
          onNextClick: () => {
            void showStep(index + 1);
          },
          onPrevClick: () => {
            void showStep(index - 1);
          },
        },
      });
      return;
    }

    // Scroll into view before highlight
    try {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      await sleep(200);
    } catch {
      /* ignore */
    }

    if (isStale(gen)) return;

    activeDriver.highlight({
      element: step.element,
      popover: {
        title: step.popover.title,
        description: step.popover.description,
        side: 'bottom',
        align: 'start',
        showButtons: ['next', 'previous', 'close'],
        nextBtnText: isLast ? 'Done' : 'Next',
        prevBtnText: 'Back',
        progressText: `${index + 1} of ${prepared.length}`,
        onNextClick: () => {
          void showStep(index + 1);
        },
        onPrevClick: () => {
          if (currentIndex > 0) void showStep(currentIndex - 1);
        },
      },
    });
  };

  await showStep(0);
}
